// SPDX-License-Identifier: MIT
//
// Mise-en-ligne wizard — the modal a user walks through when putting a generated
// app online. Two framings, selected by the DEPLOY_FRONTEND_ENABLED gate:
//
//   • gate OFF (launch / shipped-A): "Activer la base" — step ① nom court +
//     step ② besoins (a base only); confirm provisions the backend and closes.
//   • gate ON  (deploy_frontend): "Mettre en ligne mon appli" — the site is
//     ALWAYS deployed, a base is optional (a third "Juste le site" choice
//     appears), a newbie glossary explains the depth on demand, and confirm
//     shows a build-progress step that polls to a live, clickable URL.
//
// The component NEVER shows internal model names ("genesis-rest"/"full-supabase").
// The progress/live behaviour keys off the onConfirm RETURN VALUE (a deploy result
// → progress; void → close), so it works the same whether the caller deploys or
// only provisions — and is fully testable without a live deployer.
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { shortHandle, validateHandle } from '../projects/handle';
import { TIERS, fullSupabaseRequiresCloud } from '../config/backend-tiers';
import { deployFrontendEnabled } from '../config/deploy';

const URL_SUFFIX = '.genesis-platform.fr';

// A base-provisioning model (what provision-backend accepts). The wizard can also
// emit 'none' (deploy the site without a base) — see BackendModel.
export type BaseModel = 'genesis-rest' | 'full-supabase';
export type BackendModel = 'none' | BaseModel;
export type DeployStatus = 'building' | 'live' | 'failed';
export interface DeployOutcome { deployUrl: string; status: DeployStatus; logTail?: string }

export interface MiseEnLigneWizardOptions {
  title: string; // project title → shortHandle() prefill for step 1
  // Confirm handler. Return void (base-only / shipped-A) → the wizard closes.
  // Return a DeployOutcome (deploy_frontend) → the wizard shows the build-progress
  // step and polls onPollStatus until live/failed.
  onConfirm: (args: { handle: string; model: BackendModel }) => void | Promise<void | DeployOutcome>;
  onPollStatus?: () => Promise<{ status: DeployStatus; deployUrl: string | null; logTail?: string }>;
  pollIntervalMs?: number; // default 2500; tests pass a tiny value
  onCloudUpsell?: () => void; // fired (instead of onConfirm) when gate-b + full chosen
  onClose?: () => void;
}

export function renderMiseEnLigneWizard(opts: MiseEnLigneWizardOptions): HTMLElement {
  const gated = fullSupabaseRequiresCloud();
  const deploy = deployFrontendEnabled();
  const pollMs = opts.pollIntervalMs ?? 2500;
  // Default model: a base (most generated apps store data). In deploy mode the user
  // can drop down to "Juste le site" (model 'none').
  let model: BackendModel = 'genesis-rest';
  let pollTimer: ReturnType<typeof setTimeout> | null = null;

  // Framing copy switches on the deploy gate (falls back to the base keys when off).
  const k = (deployKey: string, baseKey: string) => (deploy ? deployKey : baseKey);

  const el = document.createElement('div');
  el.className = 'mel-wizard';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('data-component', 'mise-en-ligne-wizard');

  const prefill = shortHandle(opts.title);

  // The optional "Juste le site" tier — deploy mode only (site without a base).
  const siteTierHtml = deploy ? `
    <button type="button" role="radio" aria-checked="false"
            data-component="mel-tier" data-model="none"
            class="mel-wizard__tier">
      <span class="mel-wizard__tier-icon" aria-hidden="true">🌐</span>
      <span class="mel-wizard__tier-title">${escapeHtml(t('wizard.deploy.tierSiteTitle'))}</span>
      <span class="mel-wizard__tier-desc">${escapeHtml(t('wizard.deploy.tierSiteDesc'))}</span>
    </button>` : '';

  // The newbie glossary — depth explained on demand, deploy mode only.
  const glossaryHtml = deploy ? `
    <details class="mel-wizard__gloss" data-component="mel-gloss">
      <summary class="mel-wizard__gloss-toggle">${escapeHtml(t('wizard.deploy.glossaryToggle'))}</summary>
      <ul class="mel-wizard__gloss-list">
        <li>${escapeHtml(t('wizard.deploy.gloss1'))}</li>
        <li>${escapeHtml(t('wizard.deploy.gloss2'))}</li>
        <li>${escapeHtml(t('wizard.deploy.gloss3'))}</li>
        <li>${escapeHtml(t('wizard.deploy.gloss4'))}</li>
        <li>${escapeHtml(t('wizard.deploy.gloss5'))}</li>
      </ul>
    </details>` : '';

  el.innerHTML = `
    <div class="mel-wizard__card" data-step="1">
      <button type="button" data-component="mel-close" class="mel-wizard__close" aria-label="${escapeHtml(t('wizard.closeLabel'))}">×</button>

      <ol class="mel-wizard__steps" aria-hidden="true">
        <li class="mel-wizard__step" data-step-dot="1">① ${escapeHtml(t('wizard.tab1'))}</li>
        <li class="mel-wizard__step" data-step-dot="2">② ${escapeHtml(t('wizard.tab2'))}</li>
      </ol>

      <!-- ── Step 1 : nom court ─────────────────────────────────────── -->
      <section class="mel-wizard__panel" data-component="mel-panel-1">
        <p class="mel-wizard__kicker">${escapeHtml(t(k('wizard.deploy.kicker', 'wizard.kicker')))}</p>
        <h2 class="mel-wizard__title">${escapeHtml(t('wizard.step1Title'))}</h2>
        <p class="mel-wizard__hint">${escapeHtml(t('wizard.step1Hint'))}</p>

        <label class="mel-wizard__field">
          <span class="mel-wizard__label">${escapeHtml(t('wizard.handleLabel'))}</span>
          <input type="text" data-component="mel-handle-input" class="mel-wizard__input"
                 value="${escapeHtml(prefill)}" autocomplete="off" spellcheck="false"
                 maxlength="30" inputmode="url" aria-describedby="mel-preview mel-handle-error">
        </label>

        <p class="mel-wizard__preview" id="mel-preview">
          <span class="mel-wizard__preview-label">${escapeHtml(t('wizard.previewLabel'))}</span>
          <span class="mel-wizard__url">
            <strong data-component="mel-handle-echo" class="mel-wizard__url-handle">${escapeHtml(prefill)}</strong><span class="mel-wizard__url-suffix">${URL_SUFFIX}</span>
          </span>
        </p>

        <p class="mel-wizard__error" id="mel-handle-error" data-component="mel-handle-error" hidden></p>

        <div class="mel-wizard__actions">
          <button type="button" data-component="mel-next" class="mel-wizard__primary">${escapeHtml(t('wizard.next'))}</button>
        </div>
      </section>

      <!-- ── Step 2 : besoins ───────────────────────────────────────── -->
      <section class="mel-wizard__panel" data-component="mel-panel-2" hidden>
        <h2 class="mel-wizard__title">${escapeHtml(t('wizard.step2Title'))}</h2>
        <p class="mel-wizard__hint">${escapeHtml(t(k('wizard.deploy.step2Hint', 'wizard.step2Hint')))}</p>

        <div class="mel-wizard__tiers" role="radiogroup" aria-label="${escapeHtml(t('wizard.step2Title'))}">
          ${siteTierHtml}
          <button type="button" role="radio" aria-checked="true"
                  data-component="mel-tier" data-model="genesis-rest"
                  class="mel-wizard__tier mel-wizard__tier--selected">
            <span class="mel-wizard__tier-icon" aria-hidden="true">🗄️</span>
            <span class="mel-wizard__tier-title">${escapeHtml(t(TIERS.simple.titleKey))}</span>
            <span class="mel-wizard__tier-desc">${escapeHtml(t(TIERS.simple.descKey))}</span>
          </button>
          <button type="button" role="radio" aria-checked="false"
                  data-component="mel-tier" data-model="full-supabase"
                  class="mel-wizard__tier">
            ${gated ? `<span class="mel-wizard__tier-badge">${escapeHtml(t('wizard.cloudBadge'))}</span>` : ''}
            <span class="mel-wizard__tier-icon" aria-hidden="true">👤</span>
            <span class="mel-wizard__tier-title">${escapeHtml(t(TIERS.full.titleKey))}</span>
            <span class="mel-wizard__tier-desc">${escapeHtml(t(TIERS.full.descKey))}</span>
          </button>
        </div>

        <div class="mel-wizard__recap">
          <span class="mel-wizard__recap-head">${escapeHtml(t('wizard.recapHead'))}</span>
          <p class="mel-wizard__recap-body" data-component="mel-recap">${escapeHtml(recapText())}</p>
        </div>

        ${glossaryHtml}

        <p class="mel-wizard__error" data-component="mel-confirm-error" hidden></p>

        <div class="mel-wizard__actions mel-wizard__actions--split">
          <button type="button" data-component="mel-back" class="mel-wizard__secondary">${escapeHtml(t('wizard.back'))}</button>
          <button type="button" data-component="mel-confirm" class="mel-wizard__primary">${escapeHtml(t(k('wizard.deploy.confirm', 'wizard.confirm')))}</button>
        </div>
      </section>

      <!-- ── Step 3 : mise en ligne (deploy progress) ───────────────── -->
      <section class="mel-wizard__panel mel-wizard__panel--progress" data-component="mel-panel-3" hidden>
        <div data-component="mel-progress"></div>
      </section>
    </div>
  `;

  // Récap copy — prices + handle are DATA-DRIVEN; in deploy mode the recap names
  // the live address. Never hardcode prices into the flow.
  function recapText(): string {
    const handle = (el?.querySelector<HTMLInputElement>('[data-component="mel-handle-input"]')?.value.trim().toLowerCase()) || prefill;
    if (deploy) {
      return t('wizard.deploy.recapBody')
        .replace('{handle}', handle)
        .replace('{priceSimple}', TIERS.simple.cloudPrice)
        .replace('{priceFull}', TIERS.full.cloudPrice);
    }
    return t('wizard.recapBody')
      .replace('{priceSimple}', TIERS.simple.cloudPrice)
      .replace('{priceFull}', TIERS.full.cloudPrice);
  }

  const card = el.querySelector<HTMLElement>('.mel-wizard__card')!;
  const input = el.querySelector<HTMLInputElement>('[data-component="mel-handle-input"]')!;
  const echo = el.querySelector<HTMLElement>('[data-component="mel-handle-echo"]')!;
  const handleError = el.querySelector<HTMLElement>('[data-component="mel-handle-error"]')!;
  const nextBtn = el.querySelector<HTMLButtonElement>('[data-component="mel-next"]')!;
  const backBtn = el.querySelector<HTMLButtonElement>('[data-component="mel-back"]')!;
  const confirmBtn = el.querySelector<HTMLButtonElement>('[data-component="mel-confirm"]')!;
  const confirmError = el.querySelector<HTMLElement>('[data-component="mel-confirm-error"]')!;
  const recapEl = el.querySelector<HTMLElement>('[data-component="mel-recap"]')!;
  const panel1 = el.querySelector<HTMLElement>('[data-component="mel-panel-1"]')!;
  const panel2 = el.querySelector<HTMLElement>('[data-component="mel-panel-2"]')!;
  const panel3 = el.querySelector<HTMLElement>('[data-component="mel-panel-3"]')!;
  const progressHost = el.querySelector<HTMLElement>('[data-component="mel-progress"]')!;
  const stepDots = el.querySelectorAll<HTMLElement>('[data-step-dot]');

  // ── Step 1 — live preview + validation ──────────────────────────────
  function refreshStep1(): void {
    const h = input.value.trim().toLowerCase();
    echo.textContent = h || '…';
    const res = validateHandle(h);
    if (res.ok) {
      handleError.hidden = true;
      input.removeAttribute('data-state');
      nextBtn.disabled = false;
    } else {
      handleError.textContent = res.reason === 'length' ? t('wizard.errLength') : t('wizard.errCharset');
      handleError.hidden = false;
      input.setAttribute('data-state', 'error');
      nextBtn.disabled = true;
    }
  }
  input.addEventListener('input', () => { refreshStep1(); recapEl.textContent = recapText(); });
  refreshStep1();

  // ── Step navigation ─────────────────────────────────────────────────
  function goToStep(step: 1 | 2 | 3): void {
    card.setAttribute('data-step', String(step));
    panel1.hidden = step !== 1;
    panel2.hidden = step !== 2;
    panel3.hidden = step !== 3;
    stepDots.forEach((d) => {
      d.classList.toggle('mel-wizard__step--active', d.dataset.stepDot === String(step));
      d.classList.toggle('mel-wizard__step--done', step >= 2 && d.dataset.stepDot === '1');
    });
    if (step === 1) input.focus();
    else if (step === 2) { recapEl.textContent = recapText(); confirmBtn.focus(); }
  }
  nextBtn.addEventListener('click', () => { if (!nextBtn.disabled) goToStep(2); });
  backBtn.addEventListener('click', () => goToStep(1));

  // ── Step 2 — tier selection ─────────────────────────────────────────
  el.querySelectorAll<HTMLButtonElement>('[data-component="mel-tier"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      model = btn.dataset.model as BackendModel;
      el.querySelectorAll<HTMLButtonElement>('[data-component="mel-tier"]').forEach((b) => {
        const on = b === btn;
        b.classList.toggle('mel-wizard__tier--selected', on);
        b.setAttribute('aria-checked', String(on));
      });
    });
  });

  // ── Confirm ─────────────────────────────────────────────────────────
  confirmBtn.addEventListener('click', async () => {
    const handle = input.value.trim().toLowerCase();
    if (!validateHandle(handle).ok) { goToStep(1); refreshStep1(); return; }

    // Gate (b): full tier behind Cloud → route to upsell, don't provision/deploy.
    if (gated && model === 'full-supabase') { opts.onCloudUpsell?.(); close(); return; }

    confirmBtn.disabled = true;
    confirmBtn.textContent = t('wizard.confirmBusy');
    confirmError.hidden = true;
    try {
      const result = await opts.onConfirm({ handle, model });
      if (result && typeof result === 'object' && typeof result.deployUrl === 'string') {
        enterProgress(result); // deploy_frontend → build-progress + poll
      } else {
        close(); // base-only / shipped-A
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const lower = msg.toLowerCase();
      const capacity = msg.includes('429') || msg.includes('503')
        || lower.includes('capacity') || lower.includes('full') || lower.includes('quota');
      confirmError.textContent = model === 'full-supabase' && capacity
        ? t('wizard.errFullCapacity')
        : t('wizard.errProvision');
      confirmError.hidden = false;
      confirmBtn.disabled = false;
      confirmBtn.textContent = t(k('wizard.deploy.confirm', 'wizard.confirm'));
    }
  });

  // ── Step 3 — deploy progress → live / failed ────────────────────────
  function enterProgress(outcome: DeployOutcome): void {
    goToStep(3);
    paintProgress(outcome.status, outcome.deployUrl, outcome.logTail);
    if (outcome.status === 'building' && opts.onPollStatus) schedulePoll(outcome.deployUrl);
  }

  function schedulePoll(deployUrl: string): void {
    pollTimer = setTimeout(async () => {
      try {
        const s = await opts.onPollStatus!();
        const url = s.deployUrl ?? deployUrl;
        paintProgress(s.status, url, s.logTail);
        if (s.status === 'building') schedulePoll(url);
      } catch {
        // transient poll failure → keep showing building, retry next tick
        schedulePoll(deployUrl);
      }
    }, pollMs);
  }

  function paintProgress(status: DeployStatus, deployUrl: string, logTail?: string): void {
    if (status === 'building') {
      progressHost.innerHTML = `
        <div class="mel-wizard__deploy mel-wizard__deploy--building" data-deploy-state="building">
          <div class="mel-wizard__spinner" aria-hidden="true"></div>
          <h2 class="mel-wizard__title">${escapeHtml(t('wizard.deploy.building'))}</h2>
          <p class="mel-wizard__hint">${escapeHtml(t('wizard.deploy.buildingHint'))}</p>
          <p class="mel-wizard__deploy-url">${escapeHtml(stripScheme(deployUrl))}</p>
        </div>`;
    } else if (status === 'live') {
      progressHost.innerHTML = `
        <div class="mel-wizard__deploy mel-wizard__deploy--live" data-deploy-state="live">
          <div class="mel-wizard__deploy-icon" aria-hidden="true">🎉</div>
          <h2 class="mel-wizard__title">${escapeHtml(t('wizard.deploy.liveTitle'))}</h2>
          <a data-component="mel-deploy-live" class="mel-wizard__primary mel-wizard__deploy-cta"
             href="${escapeHtml(deployUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(t('wizard.deploy.liveCta'))}</a>
          <button type="button" data-component="mel-deploy-done" class="mel-wizard__secondary">${escapeHtml(t('wizard.deploy.doneClose'))}</button>
        </div>`;
      progressHost.querySelector<HTMLButtonElement>('[data-component="mel-deploy-done"]')!.addEventListener('click', close);
    } else {
      progressHost.innerHTML = `
        <div class="mel-wizard__deploy mel-wizard__deploy--failed" data-deploy-state="failed">
          <div class="mel-wizard__deploy-icon" aria-hidden="true">⚠️</div>
          <h2 class="mel-wizard__title">${escapeHtml(t('wizard.deploy.failedTitle'))}</h2>
          <p class="mel-wizard__hint">${escapeHtml(t('wizard.deploy.failedHint'))}</p>
          ${logTail ? `<details class="mel-wizard__deploy-log"><summary>${escapeHtml(t('wizard.deploy.viewLog'))}</summary><pre>${escapeHtml(logTail)}</pre></details>` : ''}
          <button type="button" data-component="mel-deploy-done" class="mel-wizard__secondary">${escapeHtml(t('wizard.deploy.doneClose'))}</button>
        </div>`;
      progressHost.querySelector<HTMLButtonElement>('[data-component="mel-deploy-done"]')!.addEventListener('click', close);
    }
  }

  function stripScheme(url: string): string { return url.replace(/^https?:\/\//, ''); }

  // ── Dismiss (backdrop / × / ESC) ────────────────────────────────────
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
  function close(): void {
    if (pollTimer) clearTimeout(pollTimer);
    document.removeEventListener('keydown', onKey);
    if (el.parentElement) el.parentElement.removeChild(el);
    opts.onClose?.();
  }
  el.addEventListener('click', (e) => { if (e.target === el) close(); });
  card.addEventListener('click', (e) => e.stopPropagation());
  el.querySelector<HTMLButtonElement>('[data-component="mel-close"]')!.addEventListener('click', close);
  document.addEventListener('keydown', onKey);

  goToStep(1);
  return el;
}
